import useAuth, { getWorkosAccessToken } from '../models/useAuth'
import { codeFailure, normalizeCode, parseCodeAnswer, type CliKind, type CodeFailure } from '../utilities/cliCode'

/**
 * The client for the router's `POST /v1/code` (nobgp-router#1859): the code a person typed or
 * scanned, turned into the session of a `nobgp login` or `nobgp register`. A plain REST route
 * with the same WorkOS bearer as `lib/register.ts`. The rules are in `utilities/cliCode.ts`.
 */
const routerApiBaseUrl =
  import.meta.env.VITE_ROUTER_API_URL?.replace(/\/$/, '') ?? 'https://router.nobgp.com'

export const routerBaseUrl = routerApiBaseUrl

export type CodeResult = { ok: true; session: string } | { ok: false; failure: CodeFailure }

/**
 * Turns the code into its session. A 401 is retried ONCE after a token refresh: a tab left open
 * across a laptop's sleep holds a token the router no longer takes, and the same stale token would
 * refuse every retry (review of app#414).
 */
export const claimCode = async (typed: string, kind: CliKind): Promise<CodeResult> => {
  const result = await claimOnce(typed, kind)
  if (result.ok || result.failure.kind !== 'unauthenticated') return result
  if (!(await useAuth.getState().refreshWorkosToken())) return result
  return claimOnce(typed, kind)
}

const claimOnce = async (typed: string, kind: CliKind): Promise<CodeResult> => {
  const code = normalizeCode(typed)
  // A code that cannot be one is a miss, said the same way, without a request.
  if (!code) return { ok: false, failure: codeFailure(404) }
  const token = getWorkosAccessToken()
  if (!token) return { ok: false, failure: codeFailure(401) }

  let response: Response
  try {
    response = await fetch(`${routerApiBaseUrl}/v1/code`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, kind }),
    })
  } catch (error) {
    console.warn('cli code: the lookup failed', error)
    return { ok: false, failure: codeFailure(0) }
  }
  if (!response.ok) return { ok: false, failure: codeFailure(response.status) }
  const session = parseCodeAnswer(await response.json().catch(() => null), kind)
  if (!session) return { ok: false, failure: codeFailure(0) }
  return { ok: true, session }
}
